import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface UseOrderStatusProps {
  orderId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook pour suivre le statut d'une commande.
 * Il vérifie toutes les 5 secondes si la commande est passée au statut "paid".
 * Arrête de vérifier après 5 minutes.
 */
export const useOrderStatus = ({ orderId, onSuccess, onError }: UseOrderStatusProps) => {
  const [status, setStatus] = useState<string>('pending');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const successCalledRef = useRef(false); // Référence pour suivre si onSuccess a déjà été appelé

  useEffect(() => {
    if (!orderId) return;

    const checkStatus = async () => {
      try {
        // Ne pas vérifier si onSuccess a déjà été appelé
        if (successCalledRef.current) {
          return;
        }
        
        setLoading(true);
        const { data, error: supaError } = await supabase
          .from('shop_orders')
          .select('status')
          .eq('id', orderId)
          .single();

        if (supaError) throw supaError;

        const currentStatus = data?.status;
        setStatus(currentStatus);

        if (currentStatus === 'paid' && !successCalledRef.current) {
          console.log('[useOrderStatus] Statut payé détecté. Premier appel à onSuccess.');
          successCalledRef.current = true; // Marquer que onSuccess a été appelé
          onSuccess?.();
          // Arrêter les vérifications après avoir détecté le statut payé
          return true;
        }
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Erreur inconnue');
        console.error('[useOrderStatus] Erreur:', e);
        setError(e);
        onError?.(e);
      } finally {
        setLoading(false);
      }
    };

    // Lancer immédiatement puis toutes les 5 s
    checkStatus();
    const interval = setInterval(checkStatus, 5000);

    // Timeout après 5 minutes
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (status !== 'paid') {
        const e = new Error('Délai de paiement dépassé');
        setError(e);
        onError?.(e);
      }
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [orderId, onSuccess, onError, status]);

  return { status, loading, error };
};
